import TemplateData from '../../utility/getTemplateData';
import {
  formatCurrency,
  formatCurrencyAsString,
  normalizeNumber,
  getGriffin,
  formatCurrencyNoDecimals,
} from '../../utility/formator';
import _get from 'lodash/get';
import AppStore from '../../stores/AppStore';
import {
  getMaximumDonationAlertMessage,
  isExternalFlow,
} from '../../utility/externalIntegrationUtils';

export const MIN_INPUT_WIDTH = 91;
export const DEFAULT_INPUT_FONTSIZE = 44;

/**
 * This is a copy of the function "formatAmountFromServer" from
 * "public/js/buttons/utils/amountUtils.js"
 */
export function formatAmountFromServerDNW(
  value,
  currency,
  { noDecimals = false } = {}
) {
  let griffin = getGriffin();
  const digits = _get(griffin, `metadata.currencyInfo[${currency}].digits`);
  const negativeDigits = `-${digits}`;

  if (value.indexOf('.') < 0) {
    if (digits === 2) {
      value = `${value}.00`;
    }
    if (digits === 1) {
      value = `${value}.0`;
    }
  } else {
    const digitsAfterDecimalPoint = value.split('.')[1].length;
    if (digitsAfterDecimalPoint === 1 && digits === 2) {
      value = `${value}0`;
    }
  }

  value = value.replace(/[^0-9]/g, '') || 0;
  value = parseInt(value, 10).toString();

  if (value.length === 1) {
    value = `00${value}`;
  } else if (value.length === 2) {
    value = `0${value}`;
  }
  // else if (value.length > MAX_AMOUNT_LENGTH) {
  // 	value = value.substr(0, MAX_AMOUNT_LENGTH);
  // }

  // Some currencies (eg: JPY) do not have decimal points
  if (digits > 0) {
    value = `${value.slice(0, negativeDigits)}.${value.slice(negativeDigits)}`;
  }

  if (
    isExternalFlow() ||
    (noDecimals && value.slice(negativeDigits) === '00')
  ) {
    return formatCurrencyNoDecimals({ value });
  }

  return formatCurrency({ value, currency });
}

export function formatAmount(value = '', currency) {
  value = value.replace(/[^0-9]/g, '') || 0;
  value = parseInt(value, 10).toString();

  if (value.length === 1) {
    value = `00${value}`;
  } else if (value.length === 2) {
    value = `0${value}`;
  }

  let griffin = getGriffin();
  const digits = _get(
    griffin,
    `metadata.currencyInfo[${TemplateData.donationCode}].digits`
  );
  const negativeDigits = `-${digits}`;

  // Some currencies (eg: JPY) do not have decimal points
  if (digits > 0) {
    value = `${value.slice(0, negativeDigits)}.${value.slice(negativeDigits)}`;
  }

  return formatCurrency({ value, currency });
}

export function amountLengthValid(value = '') {
  const val = value.replace(/[^0-9]/g, '') || 0;

  if (val.length > 9) {
    return false;
  }
  return true;
}

const allowedKeys = [16, 46, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57];
export function isKeyAllowed(keyCode) {
  return allowedKeys.indexOf(keyCode) > -1;
}

export function getMinimumDonationErrorMessage(messages, amount = 1) {
  const formattedDollarOne = formatCurrencyAsString({
    value: amount,
    currency: TemplateData.donationCode,
    maximumFractionDigits: 0,
  });

  return messages.error.minimumDonationUS.replace(
    '{amount}',
    formattedDollarOne
  );
}

/**
 * 1. Validate donation amount against the minimum donation amount
 * configured by clients or the $1 requirement for US, whichever is larger
 * 2. Validate if amount is greater than 0
 */

export function validateMinimumDonationAmount(messages) {
  // Get client configured minimum donation from token
  let minimumDonationAmount = Number(TemplateData.minimumDonationAmount) || 0;
  if (TemplateData.donationCode === 'USD') {
    // For US donations, set the minimum amount limit as the lager amount
    // of the client's configuration and $1
    minimumDonationAmount = Math.max(minimumDonationAmount, 1);
  }
  const donationAmount = normalizeNumber(AppStore.getDonationAmount());
  let showValidationError = false,
    validationErrorMessage = '',
    validationErrorCode = '';
  if (
    donationAmount !== '' &&
    minimumDonationAmount > 0 &&
    donationAmount < minimumDonationAmount
  ) {
    showValidationError = true;
    validationErrorMessage = getMinimumDonationErrorMessage(
      messages,
      minimumDonationAmount
    );
    validationErrorCode = 'MINIMUM_DONATION_AMOUNT_VALIDATION_ERROR';
  } else if (donationAmount <= 0 || donationAmount === '') {
    // Equivalent for isAmountGreaterThanZero(amount)
    showValidationError = true;
    validationErrorMessage = messages.error.amount;
    validationErrorCode = 'MINIMUM_DONATION_AMOUNT_VALIDATION_ERROR';
  }
  return { showValidationError, validationErrorMessage, validationErrorCode };
}

export function validateMaximumDonationAmount() {
  const maximumDonationAmount = Number(TemplateData.maximumDonationAmount) || 0;
  const donationAmount = normalizeNumber(AppStore.getDonationAmount());
  let showValidationError = false,
    validationErrorMessage = '',
    validationErrorCode = '';
  if (maximumDonationAmount > 0 && donationAmount > maximumDonationAmount) {
    showValidationError = true;
    validationErrorMessage = getMaximumDonationAlertMessage();
    validationErrorCode = 'MAXIMUM_DONATION_AMOUNT_VALIDATION_ERROR';
  }
  return { showValidationError, validationErrorMessage, validationErrorCode };
}

export function isAmountGreaterThanZero(amount) {
  const normalizedDonationAmount = getNormalizedNumber(amount);
  return normalizedDonationAmount > 0;
}

/**
 * Returns griffin-normalized number or zero
 */
export function getNormalizedNumber(amount) {
  try {
    if (!amount) {
      return 0;
    }

    const normalizedNumber = normalizeNumber(amount);

    if (isNaN(normalizedNumber)) {
      return 0;
    }

    if (typeof normalizedNumber !== 'number' && normalizedNumber.errors) {
      return 0;
    }

    return normalizedNumber;
  } catch (e) {
    return 0;
  }
}

/**
 * Gets the width of the input field
 * @param  {HTMLElement} input The input element
 * @param  {String} text Value of the input element
 * @return {Number}      Width of the input element
 */
export function getInputWidth(field, text) {
  text = text || '0.00';

  // Get styles from the element
  const currentStyles = window.getComputedStyle(field) || {};
  let { fontWeight } = currentStyles;
  let fontSize = `${getFontSize(text)}px`;

  // Create a span element outside visible area
  const spanElem = document.createElement('span');
  spanElem.style.fontSize = fontSize;
  spanElem.style.fontFamily = 'PayPalSansBig-Regular';
  spanElem.style.fontWeight = fontWeight || '400';
  spanElem.style.position = 'fixed';
  spanElem.style.top = '-1000px';
  spanElem.style.left = '-1000px';
  spanElem.innerHTML = text;
  document.body.appendChild(spanElem);

  // Get width of the span and then remove it
  const width = spanElem.getBoundingClientRect().width + 2; // increase by 2px to fix a layout problem in Safari iOS browser.
  document.body.removeChild(spanElem);

  if (width < MIN_INPUT_WIDTH) {
    return MIN_INPUT_WIDTH;
  }
  return width;
}

const DIGITS_TO_FONT_SIZE_MAPPING = {
  8: 42,
  9: 40,
  10: 38,
};
export function getFontSize(value) {
  let digitCount = value.replace(/[\.,]/g, '').length;

  if (digitCount < 8) {
    return DEFAULT_INPUT_FONTSIZE;
  }

  return DIGITS_TO_FONT_SIZE_MAPPING[`${digitCount}`] || 44;
}
